import { useTranslation } from 'react-i18next';
import { Maximize2 } from 'lucide-react';

import type { GateAsset } from '@/hooks/useReviewGate';
import { cn } from '@/lib/utils';

/**
 * Best-of-N picker shown at design gates: every take the crew generated, with the
 * one the AI scored highest pre-selected. Leaving it alone is a valid answer — the
 * producer only has to act if they disagree. At the clips review the takes are
 * videos: they preview on hover and play full size from the enlarge button.
 */
export default function CandidateCarousel({
  productionId,
  assets,
  selections,
  onSelect,
  onEnlarge,
  vertical,
  revision,
}: {
  productionId: string;
  assets: GateAsset[];
  selections: Record<string, number>;
  onSelect: (assetId: string, index: number) => void;
  onEnlarge: (src: string) => void;
  vertical?: boolean;
  /** Gate revision — busts the asset cache so a reworked take isn't served stale. */
  revision?: number;
}) {
  const { t } = useTranslation('production');
  if (assets.length === 0) return null;

  return (
    <div className="mt-3 space-y-3 border-t border-primary/25 pt-3">
      <p className="m-0 text-xs text-muted-foreground">{t('gate.candidates.hint')}</p>
      {assets.map((a) => {
        const active = selections[a.id] ?? a.chosen_index ?? 0;
        return (
          <div key={a.id}>
            <div className="mb-1 text-xs font-medium">{a.id}</div>
            <div className="flex flex-wrap gap-2">
              {(a.candidates || [])
                .slice()
                .sort((x, y) => x.index - y.index)
                .map((c) => {
                  const isActive = c.index === active;
                  const isAi = c.index === a.chosen_index;
                  const base = `/api/assets/${productionId}/${c.path}`;
                  const full = revision ? `${base}?rev=${revision}` : base;
                  const thumb = `${base}?thumb=1${revision ? `&rev=${revision}` : ''}`;
                  const isVideo = /\.(mp4|mov|webm)$/i.test(c.path);
                  return (
                    <button
                      key={c.index}
                      type="button"
                      onClick={() => onSelect(a.id, c.index)}
                      title={t('gate.candidates.take', { number: c.index + 1, score: c.score.toFixed(2) })}
                      className={cn(
                        'relative shrink-0 overflow-hidden rounded-lg border-2 transition-all',
                        isVideo && !vertical ? 'w-48' : 'w-32',
                        isActive ? 'border-primary ring-2 ring-primary/40' : 'border-transparent opacity-80 hover:opacity-100',
                      )}
                    >
                      {isVideo ? (
                        <video
                          src={`${full}#t=0.1`}
                          muted
                          playsInline
                          preload="metadata"
                          onMouseEnter={(e) => void e.currentTarget.play().catch(() => undefined)}
                          onMouseLeave={(e) => e.currentTarget.pause()}
                          className={cn('w-full bg-black object-cover', vertical ? 'aspect-[9/16]' : 'aspect-video')}
                        />
                      ) : (
                        <img
                          src={thumb}
                          alt={`${a.id} take ${c.index + 1}`}
                          className={cn('w-full object-cover', vertical ? 'aspect-[9/16]' : 'aspect-video')}
                        />
                      )}
                      <div className="absolute left-0 top-0 flex gap-1 p-0.5">
                        {isAi && (
                          <span className="rounded bg-black/70 px-1 py-0.5 text-[8px] font-bold text-amber-300">
                            {t('gate.candidates.aiChoice')}
                          </span>
                        )}
                        {isActive && !isAi && (
                          <span className="rounded bg-primary px-1 py-0.5 text-[8px] font-bold text-primary-foreground">
                            {t('gate.candidates.yourPick')}
                          </span>
                        )}
                      </div>
                      <span
                        role="button"
                        tabIndex={0}
                        title={t('gate.candidates.enlarge')}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEnlarge(full);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                            onEnlarge(full);
                          }
                        }}
                        className="absolute right-0 top-0 m-0.5 rounded bg-black/70 p-1 text-white opacity-80 hover:opacity-100"
                      >
                        <Maximize2 className="h-3 w-3" />
                      </span>
                      <span className="absolute bottom-0 right-0 bg-black/70 px-1 text-[8px] text-white">
                        {c.score.toFixed(2)}
                      </span>
                    </button>
                  );
                })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
